import { ReactNode, forwardRef, useId } from "react"

import { cn } from "@/lib/utils"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"

import { Input, InputProps } from "./Input"

export const Root = forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentProps<typeof RadioGroupPrimitive.Root> & {
    label?: string
  }
>(function Root({ className, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Root
      className={cn("flex flex-col gap-3", className)}
      ref={ref}
      {...props}
    />
  )
})

export const Item = forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
    label?: ReactNode
  }
>(function Item({ label, className, ...props }, ref) {
  const id = useId()
  const radioId = props.id || id

  return (
    <span
      className={cn(
        "relative flex items-center text-body1 text-colors-text-primary",
        className,
      )}
    >
      <RadioGroupPrimitive.Item
        id={radioId}
        ref={ref}
        {...props}
        className={cn(
          "focus-outline",
          "group peer flex h-3 w-3 shrink-0 items-center justify-center rounded-full",
          "border border-grey-400",
          "disabled:border-colors-text-placeholder disabled:bg-grey-200",
          "aria-checked:border-colors-icon-brand",
          "aria-checked:disabled:border-colors-text-placeholder",
        )}
      >
        <RadioGroupPrimitive.Indicator className="inline-block h-2 w-2 rounded-full bg-colors-icon-brand group-disabled:bg-colors-text-disabled" />
      </RadioGroupPrimitive.Item>
      {label ? (
        <label
          className="cursor-pointer pl-2 peer-disabled:cursor-default peer-disabled:text-colors-text-placeholder"
          htmlFor={radioId}
        >
          {label}
        </label>
      ) : null}
    </span>
  )
})

export const ItemOther = forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, "value" | "onChange"> & {
    label?: string
    value: InputProps["value"]
    onChange: InputProps["onChange"]
    placeholder?: InputProps["placeholder"]
  }
>(function OtherItem(
  { label = "Other:", value, onChange, placeholder, ...props },
  ref,
) {
  return (
    <div className="flex flex-col gap-3">
      <Item label={label} {...props} value="other" ref={ref} />
      <Input value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  )
})

export const ItemLabelOnly = forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
    label: string | ReactNode
    screenReaderText?: string
  }
>(function ItemLabelOnly({ label, screenReaderText, className, ...props }, ref) {
  const radioId = useId()
  // ItemLabelOnly hides radio control

  return (
    <span
      className={
        "relative flex items-center overflow-hidden text-body1 text-colors-text-primary"
      }
    >
      <RadioGroupPrimitive.Item id={radioId} ref={ref} {...props}>
        <label htmlFor={radioId}>
          <div
            className={cn(
              "cursor-pointer peer-disabled:cursor-default peer-disabled:text-colors-text-placeholder",
              className,
            )}
          >
            {label}
          </div>
          {/* Label text should only be for screen readers */}
          <span className="sr-only">{screenReaderText}</span>
        </label>
      </RadioGroupPrimitive.Item>
    </span>
  )
})
